import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Search, Loader2, MessageSquare, Send, Upload, FileText, Download } from "lucide-react";
import { toast } from "sonner";
import { useTurnstile, TurnstileFallback } from "../hooks/useTurnstile";

// Batch 2 §12 — Worker frontage via api.jjargent.com (custom domain).
// Set REACT_APP_WORKER_API in frontend/.env; falls back to the custom domain.
const WORKER_API = process.env.REACT_APP_WORKER_API || "https://api.jjargent.com/api";

function fmt(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: "numeric", month: "short", year: "numeric",
    });
  } catch { return iso; }
}

// ================================================================
// SearchBar — debounced GET /api/search
// ================================================================
export function HomeSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null); // null = untouched, [] = no results
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!q.trim()) {
      setResults(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    const currentQ = q;
    timerRef.current = setTimeout(() => {
      fetch(`${WORKER_API}/search?q=${encodeURIComponent(currentQ.trim())}`)
        .then((r) => r.json())
        .then((data) => {
          const rows = Array.isArray(data?.results) ? data.results : [];
          setResults(rows);
        })
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 300);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [q]);

  return (
    <section
      className="bg-[#0B0B0C] border-t border-[#343437]"
      data-testid="home-search"
      aria-labelledby="home-search-title"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-8">
          <div className="lg:col-span-7">
            <p className="eyebrow">Search the archive</p>
            <h2 id="home-search-title" className="mt-5 font-display text-3xl md:text-4xl lg:text-5xl leading-[1.02] tracking-tight text-[#FAF9F6]">
              Looking for a specific{" "}<br />
              <span className="text-[#D8A936]">note, case or comment?</span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <label htmlFor="home-search-input" className="sr-only">Search</label>
            <div className="flex items-center border border-[#343437] bg-[#151517] focus-within:border-[#D8A936] transition-colors">
              <Search size={16} className="text-[#A9A9A6] ml-4" aria-hidden />
              <input
                id="home-search-input"
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="home-search-input"
                placeholder="Try 'orientation', 'volunteers', 'AI'…"
                className="flex-1 bg-transparent text-[#FAF9F6] px-3 py-3 outline-none placeholder:text-[#6E6E6B]"
              />
              {loading && <Loader2 size={14} className="text-[#A9A9A6] animate-spin mr-4" aria-hidden />}
            </div>
          </div>
        </div>

        {results !== null && (
          <div className="mt-6" data-testid="home-search-results">
            {results.length === 0 ? (
              <p className="text-sm text-[#A9A9A6]" data-testid="home-search-empty">
                No results for "{q}".
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#343437] border border-[#343437]">
                {results.map((r, i) => (
                  <motion.article
                    key={`${r.source || "row"}-${i}-${r.posted_at || ""}`}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: Math.min(i * 0.03, 0.3) }}
                    className="bg-[#0B0B0C] p-5 hover:bg-[#151517] transition-colors"
                    data-testid={`home-search-result-${i}`}
                  >
                    <div className="flex items-baseline justify-between gap-3">
                      <p className="font-display text-[13px] text-[#FAF9F6]">{r.name || "Anonymous"}</p>
                      <span className="text-[9px] tracking-[0.18em] uppercase text-[#D8A936]">
                        {r.source || "entry"}
                      </span>
                    </div>
                    <p className="mt-3 text-xs text-[#A9A9A6] leading-relaxed line-clamp-4">{r.message}</p>
                    <p className="mt-3 text-[9px] tracking-[0.14em] uppercase text-[#6E6E6B]">{fmt(r.posted_at)}</p>
                  </motion.article>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

// ================================================================
// HomeComments — GET/POST /api/comments?page=home
// ================================================================
export function HomeComments() {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [posting, setPosting] = useState(false);
  const { containerRef: turnstileRef, token: turnstileToken, reset: resetTurnstile, scriptFailed: turnstileFailed } = useTurnstile();

  const load = useCallback(() => {
    setLoading(true);
    fetch(`${WORKER_API}/comments?page=home`)
      .then((r) => r.json())
      .then((data) => setComments(Array.isArray(data) ? data : []))
      .catch(() => setComments([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !message.trim()) return;
    if (!turnstileToken) {
      toast.error("Please complete the bot check");
      return;
    }
    setPosting(true);
    try {
      const res = await fetch(`${WORKER_API}/comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ page: "home", name: name.trim(), message: message.trim(), turnstileToken }),
      });
      const data = await res.json();
      if (data?.success) {
        toast.success("Comment posted");
        setName("");
        setMessage("");
        resetTurnstile();
        load();
      } else {
        toast.error(data?.error || "Could not post");
      }
    } catch {
      toast.error("Network error. Try again.");
    } finally {
      setPosting(false);
    }
  };

  return (
    <section
      className="bg-[#0B0B0C] border-t border-[#343437]"
      data-testid="home-comments"
      aria-labelledby="home-comments-title"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3">
              <MessageSquare size={16} className="text-[#D8A936]" aria-hidden />
              <p className="eyebrow">Leave a comment</p>
            </div>
            <h2 id="home-comments-title" className="mt-5 font-display text-3xl md:text-4xl leading-[1.02] tracking-tight text-[#FAF9F6]">
              What resonated?{" "}<br />
              <span className="text-[#D8A936]">Say it out loud.</span>
            </h2>
            <p className="mt-4 text-sm text-[#A9A9A6] leading-relaxed max-w-md">
              A short reaction to something on this page — a case study, a stat, a claim. Kept
              visible for other readers.
            </p>

            <form onSubmit={submit} className="mt-8 space-y-4" data-testid="home-comments-form">
              <div>
                <label htmlFor="hc-name" className="text-[10px] tracking-[0.22em] uppercase text-[#A9A9A6]">Name</label>
                <input
                  id="hc-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  maxLength={80}
                  data-testid="home-comments-name"
                  className="mt-2 w-full bg-[#151517] border border-[#343437] px-4 py-3 text-[#FAF9F6] outline-none focus:border-[#D8A936] transition-colors"
                />
              </div>
              <div>
                <label htmlFor="hc-msg" className="text-[10px] tracking-[0.22em] uppercase text-[#A9A9A6]">Comment</label>
                <textarea
                  id="hc-msg"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                  maxLength={500}
                  rows={3}
                  data-testid="home-comments-message"
                  className="mt-2 w-full bg-[#151517] border border-[#343437] px-4 py-3 text-[#FAF9F6] outline-none focus:border-[#D8A936] transition-colors resize-none"
                />
                <p className="mt-1 text-[10px] text-[#A9A9A6] text-right">{message.length}/500</p>
              </div>
              <button
                type="submit"
                disabled={posting}
                data-testid="home-comments-submit"
                className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {posting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                {posting ? "Posting…" : "Post comment"}
              </button>
              <div ref={turnstileRef} data-testid="home-comments-turnstile" className="mt-2" />
              <TurnstileFallback show={turnstileFailed} />
            </form>
          </div>

          <div className="lg:col-span-7">
            <p className="eyebrow">Recent comments</p>
            <h3 className="mt-5 font-display text-2xl md:text-3xl leading-tight text-[#FAF9F6]">
              <span className="text-[#D8A936]">{comments.length}</span>{" "}
              {comments.length === 1 ? "voice" : "voices"} so far
            </h3>

            <div className="mt-6 space-y-4" data-testid="home-comments-list">
              {loading && <p className="text-sm text-[#A9A9A6]">Loading…</p>}
              {!loading && comments.length === 0 && (
                <p className="text-sm text-[#A9A9A6]" data-testid="home-comments-empty">
                  Be the first to comment on this page.
                </p>
              )}
              {!loading && comments.map((c, i) => (
                <motion.article
                  key={c.id || `c-${i}-${c.posted_at || ""}`}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: Math.min(i * 0.03, 0.3) }}
                  className="border border-[#343437] bg-[#151517]/60 p-5 hover:border-[#D8A936] transition-colors"
                  data-testid={`home-comment-${i}`}
                >
                  <div className="flex items-baseline justify-between gap-3">
                    <p className="font-display text-[15px] text-[#FAF9F6]">{c.name}</p>
                    <p className="text-[10px] tracking-[0.14em] uppercase text-[#A9A9A6]">{fmt(c.posted_at)}</p>
                  </div>
                  <p className="mt-3 text-[15px] text-[#FAF9F6] leading-relaxed whitespace-pre-wrap">{c.message}</p>
                </motion.article>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ================================================================
// FileUploads — POST /api/upload + GET /api/uploads
// ================================================================
export function FileUploads() {
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    fetch(`${WORKER_API}/uploads`)
      .then((r) => r.json())
      .then((data) => setUploads(Array.isArray(data) ? data : []))
      .catch(() => setUploads([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(`${WORKER_API}/upload`, { method: "POST", body: form });
      const data = await res.json();
      if (data?.success) {
        toast.success(`Uploaded ${data.filename || file.name}`);
        load();
      } else {
        toast.error(data?.error || "Upload failed");
      }
    } catch {
      toast.error("Upload failed. Try again.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const kb = (bytes) => {
    if (typeof bytes !== "number") return "—";
    return `${(bytes / 1024).toFixed(1)} KB`;
  };

  return (
    <section className="bg-[#0B0B0C] border-t border-[#343437]" data-testid="file-uploads">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3">
              <Upload size={16} className="text-[#D8A936]" aria-hidden />
              <p className="eyebrow">Drop a file</p>
            </div>
            <h2 className="mt-5 font-display text-3xl md:text-4xl leading-tight text-[#FAF9F6]">
              A workspace for shared docs
            </h2>
            <p className="mt-4 text-sm text-[#A9A9A6] leading-relaxed max-w-md">
              Sharing a brief, an image, a policy? Upload it here and I'll see it in the Monday
              pulse. Anyone with the link can download.
            </p>

            <div className="mt-8 border border-dashed border-[#343437] hover:border-[#D8A936] p-8 text-center transition-colors">
              <input
                ref={inputRef}
                type="file"
                onChange={(e) => upload(e.target.files?.[0])}
                disabled={uploading}
                data-testid="file-upload-input"
                id="file-upload-input"
                className="hidden"
              />
              <label
                htmlFor="file-upload-input"
                data-testid="file-upload-label"
                className={`btn-primary inline-flex ${uploading ? "opacity-60 pointer-events-none" : "cursor-pointer"}`}
              >
                {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                {uploading ? "Uploading…" : "Choose file"}
              </label>
              <p className="mt-4 text-[10px] tracking-[0.16em] uppercase text-[#A9A9A6]">
                Attach a file
              </p>
            </div>
          </div>

          <div className="lg:col-span-7">
            <p className="eyebrow">Shared files</p>
            <h3 className="mt-5 font-display text-2xl md:text-3xl leading-tight text-[#FAF9F6]">
              <span className="text-[#D8A936]">{uploads.length}</span>{" "}
              {uploads.length === 1 ? "file" : "files"} on the shelf
            </h3>

            <div className="mt-6 space-y-2" data-testid="file-uploads-list">
              {loading && <p className="text-sm text-[#A9A9A6]">Loading…</p>}
              {!loading && uploads.length === 0 && (
                <p className="text-sm text-[#A9A9A6]" data-testid="file-uploads-empty">Nothing yet.</p>
              )}
              {!loading && uploads.map((u, i) => (
                <motion.a
                  key={u.id || `u-${i}`}
                  href={`${WORKER_API}/file/${encodeURIComponent(u.filename)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.28, delay: Math.min(i * 0.03, 0.3) }}
                  data-testid={`file-upload-row-${i}`}
                  className="flex items-center gap-4 border border-[#343437] bg-[#151517]/60 p-4 hover:border-[#D8A936] hover:bg-[#151517] transition-colors group"
                >
                  <FileText size={16} className="text-[#D8A936] shrink-0" aria-hidden />
                  <div className="flex-1 min-w-0">
                    <p className="font-display text-sm text-[#FAF9F6] truncate group-hover:text-[#D8A936] transition-colors">
                      {u.filename}
                    </p>
                    <p className="text-[10px] tracking-[0.14em] uppercase text-[#A9A9A6] mt-1">
                      {u.file_type || "file"} · {kb(u.file_size)} · {fmt(u.uploaded_at)}
                    </p>
                  </div>
                  <Download size={14} className="text-[#A9A9A6] group-hover:text-[#D8A936] transition-colors shrink-0" aria-hidden />
                </motion.a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
